/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import PropTypes from 'prop-types';
import { Radio, RefreshCw, WifiOff } from 'lucide-react';

// Whether the live session server link is down (retrying or given up) -- the
// session list can't be trusted, so consumers swap in the notice below.
export const isLiveSessionServerOffline = (serverStatus) => (
  serverStatus === 'reconnecting' || serverStatus === 'disconnected'
);

// Icon + colors for the Live Sessions entry points (sidebar button, dashboard card header).
export const getLiveSessionIndicator = (serverStatus) => {
  if (serverStatus === 'disconnected') {
    return {
      Icon: WifiOff,
      iconClass: '',
      boxClass: 'bg-error/10 text-error',
      label: 'Server disconnected',
    };
  }
  if (serverStatus === 'reconnecting') {
    return {
      Icon: RefreshCw,
      iconClass: 'animate-spin',
      boxClass: 'bg-warning/10 text-warning',
      label: 'Reconnecting…',
    };
  }
  return {
    Icon: Radio,
    iconClass: '',
    boxClass: 'bg-primary/10 text-primary',
    label: null,
  };
};

const NOTICE_CONTENT = {
  reconnecting: {
    Icon: RefreshCw,
    iconClass: 'text-warning animate-spin',
    beaconClass: 'bg-warning/10 border-warning/20',
    dotClass: 'bg-warning',
    pill: 'Reconnecting…',
    heading: 'Connection Interrupted',
    text: 'Lost connection to the live session server. Trying to reconnect — live sessions will reappear automatically.',
  },
  disconnected: {
    Icon: WifiOff,
    iconClass: 'text-error',
    beaconClass: 'bg-error/10 border-error/20',
    dotClass: 'bg-error',
    pill: 'Server Disconnected',
    heading: 'Live Session Server Unavailable',
    text: 'Unable to connect to the live session server, so live sessions can\'t be shown right now. Please refresh the page or try again later.',
  },
};

const LiveSessionServerNotice = ({ status, compact = false, onReconnect, reconnectAttemptsLeft = 0 }) => {
  const content = NOTICE_CONTENT[status] || NOTICE_CONTENT.disconnected;
  const { Icon } = content;
  const showReconnect = status === 'disconnected' && !!onReconnect;
  const reconnectExhausted = reconnectAttemptsLeft <= 0;

  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center py-6 sm:py-8 px-4">
      <div className={`relative flex items-center justify-center ${compact ? 'mb-3.5' : 'mb-5'}`}>
        <div className={`relative ${compact ? 'w-12 h-12' : 'w-16 h-16'} rounded-2xl border flex items-center justify-center shadow-inner ${content.beaconClass}`}>
          <Icon size={compact ? 22 : 30} className={content.iconClass} />
        </div>
      </div>

      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full bg-base-200 border border-base-content/10 font-bold text-base-content/70 shadow-sm ${compact ? 'text-[10px] sm:text-[11px] mb-2.5' : 'text-[11px] mb-4'}`}>
        <span className={`inline-flex rounded-full h-2 w-2 ${content.dotClass}`} />
        <span className="uppercase tracking-wider">{content.pill}</span>
      </div>

      <h3 className={`font-black text-base-content tracking-tight ${compact ? 'text-sm sm:text-base mb-1' : 'text-xl sm:text-2xl mb-2'}`}>
        {content.heading}
      </h3>
      <p className={`text-base-content/60 font-medium mx-auto leading-relaxed ${compact ? 'text-xs max-w-sm' : 'text-xs sm:text-sm max-w-md'}`}>
        {content.text}
      </p>

      {showReconnect && (
        <div className={`flex flex-col items-center gap-1.5 ${compact ? 'mt-4' : 'mt-6'}`}>
          <button
            type="button"
            onClick={onReconnect}
            disabled={reconnectExhausted}
            className="btn btn-sm btn-primary gap-1.5 disabled:opacity-40"
            title={reconnectExhausted ? 'Reconnect limit reached' : 'Reconnect to the live session server'}
          >
            <RefreshCw size={14} />
            Reconnect
          </button>
          {reconnectExhausted && (
            <span className="text-[11px] font-medium text-base-content/50">
              Reconnect limit reached. Please log out and sign in again to retry.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

LiveSessionServerNotice.propTypes = {
  status: PropTypes.oneOf(['reconnecting', 'disconnected']).isRequired,
  compact: PropTypes.bool,
  onReconnect: PropTypes.func,
  reconnectAttemptsLeft: PropTypes.number,
};

export default LiveSessionServerNotice;
